/**
 * AI Interview Service
 *
 * Manages AI-powered video interview sessions for Virtual Assistants.
 * Follows the same pattern as aiSkillsAssessmentService.js with:
 * - Supabase DB for session management
 * - Gemini Edge Functions for question generation & evaluation
 * - JSONB conversation logs
 * - Score calculation and profile updates
 *
 * Features:
 *  - VA self-initiated interviews
 *  - Employer-requested interviews
 *  - 5-round personalized questions from VA profile data
 *  - Video chunk URL tracking
 *  - Final AI evaluation summary
 */

import supabase from '../utils/supabase';
import { SupabaseError } from '../utils/errorHandling';
import {
  generateInterviewQuestion,
} from '../utils/aiService';

const TOTAL_ROUNDS   = parseInt(import.meta.env.VITE_AI_INTERVIEW_ROUNDS)           || 5;
const COOLDOWN_HOURS = parseInt(import.meta.env.VITE_AI_INTERVIEW_COOLDOWN_HOURS)    || 24;
const STORAGE_BUCKET = 'interview-videos';

/**
 * Normalize a user's full name into a safe storage folder slug.
 * e.g. "Jane Doe" → "jane-doe", "María José" → "maria-jose"
 * Falls back to the provided fallback string (e.g. UUID) when empty.
 */
function sanitizeFolderName(name, fallback = 'unknown') {
  if (!name) return fallback;
  const slug = name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')   // strip accent marks
    .replace(/[^a-z0-9]+/g, '-')       // non-alphanumeric → hyphen
    .replace(/^-+|-+$/g, '');           // trim edge hyphens
  return slug || fallback;
}

class AIInterviewService {
  // ─── Session Management ───────────────────────────────────────────────────

  /**
   * Start a new AI interview session.
   * @param {string} vaId       - VA user ID
   * @param {Object} vaProfile  - Full VA profile object
   * @param {Object|null} jobContext  - { id, title, required_skills } or null for self-initiated
   * @param {string|null} employerId  - Employer ID if employer-requested
   * @returns {Promise<Object>} { sessionId, question, questionType, focusArea, round }
   */
  async startInterview(vaId, vaProfile, jobContext = null, employerId = null) {
    try {
      // 1. Cooldown check (24 hours between sessions)
      const cooldownCheck = await this.checkCooldown(vaId);
      if (!cooldownCheck.allowed) {
        const err = new Error(cooldownCheck.message);
        err.inProgressSessionId = cooldownCheck.inProgressSessionId;
        throw err;
      }

      // 2. Generate first question from Gemini

      const questionData = await generateInterviewQuestion(vaProfile, jobContext, [], 1);

      if (!questionData?.question) {
        throw new Error('Failed to generate the first interview question. Please try again.');
      }

      // 3. Build initial conversation log
      const initialLog = [{
        type:         'ai',
        content:      questionData.question,
        round:        1,
        questionType: questionData.questionType || 'introduction',
        focusArea:    questionData.focusArea    || 'Introduction',
        timestamp:    new Date().toISOString(),
      }];

      // 4. Insert session record
      const { data: session, error } = await supabase
        .from('ai_interview_sessions')
        .insert({
          va_id:          vaId,
          employer_id:    employerId,
          job_id:         jobContext?.id || null,
          status:         'in_progress',
          conversation_log: initialLog,
          total_rounds:   TOTAL_ROUNDS,
          rounds_completed: 0,
          ai_model_used:  import.meta.env.VITE_AI_MODEL || 'gemini-2.5-flash',
          started_at:     new Date().toISOString(),
        })
        .select()
        .single();

      if (error) {
        throw new SupabaseError(error.message, error, {
          table:  'ai_interview_sessions',
          action: 'insert',
        });
      }

      // 5. If there was a pending interview_request, link it
      if (employerId && jobContext?.id) {
        await supabase
          .from('interview_requests')
          .update({ session_id: session.id, status: 'accepted' })
          .eq('va_id',       vaId)
          .eq('employer_id', employerId)
          .eq('job_id',      jobContext.id)
          .eq('status',      'pending');
      }

      return {
        sessionId:    session.id,
        question:     questionData.question,
        questionType: questionData.questionType || 'introduction',
        focusArea:    questionData.focusArea    || 'Introduction',
        round:        1,
        totalRounds:  TOTAL_ROUNDS,
        progress:     1 / TOTAL_ROUNDS,
      };
    } catch (err) {
      throw err;
    }
  }

  /**
   * Submit an answer for the current round and receive the next question or final results.
   * @param {string} sessionId  - Interview session ID
   * @param {string} answer     - VA's text answer (transcript or typed)
   * @param {string} vaProfile  - VA profile for follow-up question context
   * @param {string|null} videoChunkUrl - Optional URL of uploaded video chunk
   * @returns {Promise<Object>} { done, question?, evaluation, round, progress } | { done, result }
   */
  async submitResponse(sessionId, answer, vaProfile, videoChunkUrl = null, videoStoragePath = null) {
    try {
      // 1. Load session
      const { data: session, error: fetchErr } = await supabase
        .from('ai_interview_sessions')
        .select('*')
        .eq('id', sessionId)
        .single();

      if (fetchErr) {
        throw new SupabaseError(fetchErr.message, fetchErr, {
          table: 'ai_interview_sessions', action: 'fetch',
        });
      }
      if (!session) throw new Error('Interview session not found.');

      const log          = session.conversation_log || [];
      const completedRounds = log.filter(e => e.type === 'user').length;
      const currentRound    = completedRounds + 1;

      // 2. Append user entry to log (no evaluation — answers proceed directly)
      const userEntry = {
        type:            'user',
        content:         answer,
        round:           currentRound,
        videoChunkUrl:   videoChunkUrl   || null,
        videoStoragePath: videoStoragePath || null,
        timestamp:       new Date().toISOString(),
      };

      const updatedLog = [...log, userEntry];

      // 3. Determine if interview is done
      const isDone = currentRound >= TOTAL_ROUNDS;

      if (isDone) {
        return await this._finalizeInterview(session, updatedLog);
      }

      // 4. Generate next question
      const nextRound = currentRound + 1;
      const nextQuestion = await generateInterviewQuestion(
        vaProfile,
        session.job_id ? { id: session.job_id } : null,
        updatedLog,
        nextRound
      );

      if (!nextQuestion?.question) {
        throw new Error('Failed to generate next question. Please try again.');
      }

      const aiEntry = {
        type:         'ai',
        content:      nextQuestion.question,
        round:        nextRound,
        questionType: nextQuestion.questionType || 'general',
        focusArea:    nextQuestion.focusArea    || '',
        timestamp:    new Date().toISOString(),
      };

      const finalLog = [...updatedLog, aiEntry];

      // 5. Persist updated log
      const { error: updateErr } = await supabase
        .from('ai_interview_sessions')
        .update({
          conversation_log:  finalLog,
          rounds_completed:  currentRound,
        })
        .eq('id', sessionId);

      if (updateErr) {
        throw new SupabaseError(updateErr.message, updateErr, {
          table: 'ai_interview_sessions', action: 'update',
        });
      }

      return {
        done:         false,
        question:     nextQuestion.question,
        questionType: nextQuestion.questionType || 'general',
        focusArea:    nextQuestion.focusArea    || '',
        round:        nextRound,
        totalRounds:  TOTAL_ROUNDS,
        progress:     nextRound / TOTAL_ROUNDS,
      };
    } catch (err) {
      throw err;
    }
  }

  /**
   * Attach the final video URL to a completed session.
   * @param {string} sessionId
   * @param {string} videoUrl      - Signed Supabase Storage URL
   * @param {string} storagePath   - Storage path for permanent reference
   * @param {number} durationSecs
   */
  async attachVideo(sessionId, videoUrl, storagePath, durationSecs = 0) {
    // Derive the top-level folder from the storage path (first path segment)
    const storageFolder = storagePath ? storagePath.split('/')[0] : null;

    const { error } = await supabase
      .from('ai_interview_sessions')
      .update({
        video_url:           videoUrl,
        video_duration_secs: durationSecs,
        ...(storageFolder ? { storage_folder: storageFolder } : {}),
      })
      .eq('id', sessionId);

    if (error) {
      throw new SupabaseError(error.message, error, {
        table: 'ai_interview_sessions', action: 'update-video',
      });
    }
  }

  /**
   * Fetch a single interview session (for results view).
   * @param {string} sessionId
   */
  async getSession(sessionId) {
    const { data, error } = await supabase
      .from('ai_interview_sessions')
      .select('*')
      .eq('id', sessionId)
      .single();

    if (error) {
      throw new SupabaseError(error.message, error, {
        table: 'ai_interview_sessions', action: 'fetch',
      });
    }

    return data;
  }

  /**
   * List all interview sessions for a VA (dashboard view).
   */
  async getVAInterviews(vaId) {
    const { data, error } = await supabase
      .from('ai_interview_sessions')
      .select('id, status, final_score, overall_rating, video_url, video_duration_secs, rounds_completed, total_rounds, created_at, completed_at, job_id, is_public')
      .eq('va_id', vaId)
      .neq('status', 'abandoned') // Exclude deleted sessions
      .order('created_at', { ascending: false });

    if (error) {
      throw new SupabaseError(error.message, error, {
        table: 'ai_interview_sessions', action: 'list',
      });
    }

    return data || [];
  }

  /**
   * List completed interviews requested by an employer.
   */
  async getEmployerInterviews(employerId) {
    const { data, error } = await supabase
      .from('ai_interview_sessions')
      .select(`
        id, status, final_score, overall_rating, video_url, video_duration_secs,
        created_at, completed_at, job_id, va_id,
        va:va_id ( full_name, profile_picture, industry_specialization ),
        job:job_id ( title )
      `)
      .eq('employer_id', employerId)
      .in('status', ['completed', 'in_progress'])
      .order('created_at', { ascending: false });

    if (error) {
      throw new SupabaseError(error.message, error, {
        table: 'ai_interview_sessions', action: 'employer-list',
      });
    }

    return data || [];
  }

  // ─── Interview Requests ───────────────────────────────────────────────────

  /**
   * Employer sends an interview request to a VA.
   */
  async requestInterview(employerId, vaId, jobId, message = '') {
    const { data, error } = await supabase
      .from('interview_requests')
      .insert({
        employer_id: employerId,
        va_id:       vaId,
        job_id:      jobId || null,
        message,
        status:      'pending',
        expires_at:  new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
      })
      .select()
      .single();

    if (error) {
      throw new SupabaseError(error.message, error, {
        table: 'interview_requests', action: 'insert',
      });
    }

    return data;
  }

  /**
   * Get pending interview requests for a VA.
   */
  async getPendingRequestsForVA(vaId) {
    const { data, error } = await supabase
      .from('interview_requests')
      .select(`
        id, status, message, expires_at, created_at, job_id,
        employer:employer_id ( full_name, profile_picture ),
        job:job_id ( title )
      `)
      .eq('va_id', vaId)
      .eq('status', 'pending')
      .gt('expires_at', new Date().toISOString())
      .order('created_at', { ascending: false });

    if (error) {
      throw new SupabaseError(error.message, error, {
        table: 'interview_requests', action: 'list-pending',
      });
    }

    return data || [];
  }

  /**
   * Check whether a pending request exists from an employer to a VA for a job.
   */
  async checkRequestExists(employerId, vaId, jobId) {
    const { data } = await supabase
      .from('interview_requests')
      .select('id, status')
      .eq('employer_id', employerId)
      .eq('va_id', vaId)
      .eq('job_id', jobId)
      .in('status', ['pending', 'accepted'])
      .maybeSingle();

    return data || null;
  }

  // ─── Cooldown ─────────────────────────────────────────────────────────────

  async checkCooldown(vaId) {
    const since = new Date(Date.now() - COOLDOWN_HOURS * 60 * 60 * 1000).toISOString();

    const { data, error } = await supabase
      .from('ai_interview_sessions')
      .select('id, created_at')
      .eq('va_id', vaId)
      .eq('status', 'in_progress')
      .gte('created_at', since)
      .limit(1);

    if (error) {
      return { allowed: true };
    }

    if (data && data.length > 0) {
      const nextTime = new Date(new Date(data[0].created_at).getTime() + COOLDOWN_HOURS * 60 * 60 * 1000);
      const hoursLeft = Math.ceil((nextTime - Date.now()) / (1000 * 60 * 60));
      return {
        allowed: false,
        inProgressSessionId: data[0].id,
        message: `You have an interview in progress. Please complete it or wait ${hoursLeft} hour(s) before starting a new one.`,
      };
    }

    return { allowed: true };
  }

  /**
   * Permanently delete an interview session and all associated storage files.
   * @param {string} sessionId
   * @returns {Promise<void>}
   */
  async deleteInterview(sessionId) {
    // 1. Fetch the session to get storage metadata + conversation log
    const { data: session } = await supabase
      .from('ai_interview_sessions')
      .select('id, va_id, storage_folder, conversation_log')
      .eq('id', sessionId)
      .maybeSingle();

    // 2. Remove storage files (best-effort — never block DB delete on storage errors)
    if (session) {
      await this._deleteSessionStorage(session).catch((err) =>
        console.warn('[aiInterviewService] Storage cleanup warning:', err.message)
      );
    }

    // 3. Hard-delete the DB record
    const { error } = await supabase
      .from('ai_interview_sessions')
      .delete()
      .eq('id', sessionId);

    if (error) {
      throw new SupabaseError(error.message, error, {
        table:  'ai_interview_sessions',
        action: 'delete',
      });
    }
  }

  /**
   * List and delete every file stored under {storage_folder}/{sessionId}/ in the
   * interview-videos bucket.  Collects paths from both the storage listing and any
   * videoStoragePath entries embedded in the conversation_log, so no orphaned files
   * are left behind even if the bucket listing misses in-flight chunk uploads.
   * @param {{ id: string, va_id: string, storage_folder: string|null, conversation_log: Array }} session
   */
  async _deleteSessionStorage(session) {
    const folder    = session.storage_folder || session.va_id;
    const sessionId = session.id;
    const prefix    = `${folder}/${sessionId}`;

    const pathSet = new Set();

    // a) List all files Supabase knows about in this subfolder
    const { data: listed } = await supabase.storage
      .from(STORAGE_BUCKET)
      .list(prefix);

    if (listed?.length) {
      listed.forEach((f) => pathSet.add(`${prefix}/${f.name}`));
    }

    // b) Collect any extra paths recorded in the conversation log
    const log = session.conversation_log || [];
    log.forEach((entry) => {
      if (entry.videoStoragePath) pathSet.add(entry.videoStoragePath);
    });

    if (pathSet.size > 0) {
      const { error } = await supabase.storage
        .from(STORAGE_BUCKET)
        .remove([...pathSet]);

      if (error) {
        console.warn('[aiInterviewService] Failed to remove storage files:', error.message);
      }
    }
  }

  /**
   * One-time migration: rename storage files for all sessions that still use the
   * UUID-based folder structure ({va_id}/{session_id}/…) to the full-name slug
   * ({sanitized_name}/{session_id}/…), update the video_url signed URL and
   * conversation log storage paths in the DB, then update storage_folder.
   *
   * Safe to call multiple times — skips sessions already on the new path.
   *
   * @param {string} vaId          - The VA's user ID
   * @param {string} vaFullName    - The VA's current full_name
   * @returns {Promise<{ migrated: number, skipped: number, errors: number }>}
   */
  async migrateStoragePaths(vaId, vaFullName) {
    const newFolder = sanitizeFolderName(vaFullName, vaId);
    let migrated = 0, skipped = 0, errors = 0;

    // Fetch all this VA's sessions that still point to the UUID folder
    const { data: sessions, error: fetchErr } = await supabase
      .from('ai_interview_sessions')
      .select('id, video_url, conversation_log, storage_folder')
      .eq('va_id', vaId)
      .neq('storage_folder', newFolder); // already migrated sessions are skipped

    if (fetchErr) {
      console.error('[aiInterviewService] migrateStoragePaths fetch error:', fetchErr);
      return { migrated, skipped, errors: 1 };
    }

    for (const session of sessions || []) {
      const oldFolder = session.storage_folder || vaId;
      if (oldFolder === newFolder) { skipped++; continue; }

      try {
        const prefix    = `${oldFolder}/${session.id}`;
        const newPrefix = `${newFolder}/${session.id}`;

        // 1. List all files under the old prefix
        const { data: files } = await supabase.storage
          .from(STORAGE_BUCKET)
          .list(prefix);

        if (!files?.length) {
          // No files to move — just update the folder reference
          await supabase.from('ai_interview_sessions')
            .update({ storage_folder: newFolder })
            .eq('id', session.id);
          skipped++;
          continue;
        }

        // 2. Copy each file to the new path
        const updatedLog = JSON.parse(JSON.stringify(session.conversation_log || []));
        let newFinalVideoUrl  = null;
        let newFinalPath      = null;

        for (const file of files) {
          const oldPath = `${prefix}/${file.name}`;
          const newPath = `${newPrefix}/${file.name}`;

          const { error: copyErr } = await supabase.storage
            .from(STORAGE_BUCKET)
            .copy(oldPath, newPath);

          if (copyErr) throw new Error(`copy ${oldPath}: ${copyErr.message}`);

          // Track the final video file so we can refresh its signed URL
          if (file.name.includes('final')) {
            newFinalPath = newPath;
          }

          // Update any matching videoStoragePath entries in the conversation log
          updatedLog.forEach((entry) => {
            if (entry.videoStoragePath === oldPath) {
              entry.videoStoragePath = newPath;
            }
          });
        }

        // 3. Generate a fresh 1-year signed URL for the final video
        let newVideoUrl = session.video_url;
        if (newFinalPath) {
          const { data: signed, error: signErr } = await supabase.storage
            .from(STORAGE_BUCKET)
            .createSignedUrl(newFinalPath, 31_536_000);
          if (!signErr) newVideoUrl = signed.signedUrl;
        }

        // 4. Persist updated metadata in DB
        await supabase
          .from('ai_interview_sessions')
          .update({
            storage_folder:   newFolder,
            video_url:        newVideoUrl,
            conversation_log: updatedLog,
          })
          .eq('id', session.id);

        // 5. Delete the old files
        const oldPaths = files.map((f) => `${prefix}/${f.name}`);
        await supabase.storage.from(STORAGE_BUCKET).remove(oldPaths);

        migrated++;
      } catch (err) {
        console.error(`[aiInterviewService] migrateStoragePaths error for session ${session.id}:`, err);
        errors++;
      }
    }

    return { migrated, skipped, errors };
  }

  // ─── Public Profile ───────────────────────────────────────────────────────

  /**
   * Fetch the most recent public completed interview for a VA's public profile.
   * Accessible without authentication (RLS: is_public = TRUE AND status = 'completed').
   * @param {string} vaId
   * @returns {Promise<Object|null>}
   */
  async getPublicInterviewForVA(vaId) {
    const { data, error } = await supabase
      .from('ai_interview_sessions')
      .select('id, video_url, video_duration_secs, conversation_log, rounds_completed, total_rounds, completed_at, created_at, is_public')
      .eq('va_id', vaId)
      .eq('status', 'completed')
      .eq('is_public', true)
      .order('completed_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error('[aiInterviewService] getPublicInterviewForVA error:', error);
      return null;
    }

    return data || null;
  }

  /**
   * Toggle the is_public flag on a completed interview session.
   * Only the owning VA can call this (enforced by RLS va_id = auth.uid()).
   * @param {string} sessionId
   * @param {boolean} isPublic
   */
  async setInterviewPublic(sessionId, isPublic) {
    const { error } = await supabase
      .from('ai_interview_sessions')
      .update({ is_public: isPublic })
      .eq('id', sessionId);

    if (error) {
      throw new SupabaseError(error.message, error, {
        table:  'ai_interview_sessions',
        action: 'toggle-public',
      });
    }
  }

  // ─── Private: Finalize & score ────────────────────────────────────────────

  async _finalizeInterview(session, finalLog) {
    try {
      // Persist final state (no evaluation/scoring)
      const { error } = await supabase
        .from('ai_interview_sessions')
        .update({
          status:           'completed',
          conversation_log: finalLog,
          rounds_completed: session.total_rounds,
          completed_at:     new Date().toISOString(),
        })
        .eq('id', session.id);

      if (error) {
        throw new SupabaseError(error.message, error, {
          table: 'ai_interview_sessions', action: 'finalize',
        });
      }

      // Fetch refreshed session for return
      const { data: completed } = await supabase
        .from('ai_interview_sessions')
        .select('*')
        .eq('id', session.id)
        .single();

      return {
        done:     true,
        session:  completed,
        progress: 1,
      };
    } catch (err) {
      throw err;
    }
  }
}

const aiInterviewService = new AIInterviewService();
export default aiInterviewService;
